import { FC, useEffect, useState, useRef } from 'react';
import { EditorProvider } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { Box } from '@mui/material';
import { MenuBar } from './MenuBar';
import styles from './RichTextEditor.module.css';

const extensions = [
  StarterKit.configure({
    bulletList: {
      keepMarks: true,
      keepAttributes: true,
    },
    orderedList: {
      keepMarks: true,
      keepAttributes: true, 
    },
  }),
  Underline,
];

interface RichTextEditorProps {
  onUpdate?: (data: string) => void;
  data?: string;
  readOnly?: boolean;
  fillHeight?: boolean;
}

export const RichTextEditor: FC<RichTextEditorProps> = ({ 
  onUpdate,
  data = '',
  readOnly = false,
  fillHeight = false
}) => {
  const [content, setContent] = useState<string>(data);
  const editorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (data !== content) {
      setContent(data);
    }
  }, [data, setContent, content]);

  useEffect(() => {
    if (fillHeight && editorRef.current) {
      const editorContent = editorRef.current.querySelector('.ProseMirror');
      if (editorContent instanceof HTMLElement) {
        editorContent.style.height = '100%';
        editorContent.style.minHeight = '300px';
        editorContent.style.overflow = 'auto';
        editorContent.style.pointerEvents = 'none';
      }
    }
  }, [fillHeight, content]);

  if (!content && data !== '') {
    return null;
  }

  return (
    <Box 
      ref={editorRef} 
      className={`${styles.editorContainer} ${fillHeight ? styles.fillHeight : ''}`}
    >
      <EditorProvider
        slotBefore={readOnly ? null : <MenuBar />}
        extensions={extensions}
        content={content}
        editable={!readOnly}
        onUpdate={({ editor }) => {
          const newContent = editor.getHTML();
          setContent(newContent);
          onUpdate?.(newContent);
        }}
        editorProps={{
          attributes: {
            class: 'rich-text-content',
          }
        }}
      />
    </Box>
  );
};

